import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Link as DSLink,
  Loader,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  DetailsHeader,
  EntitlementDetails,
  // SodDetailsSidePanel,
  ViolationsTabDetails,
} from '../../features';
import {
  useHandleMitigatingControl,
  useHydrateViolationsDetailsSession,
} from '../../features/Details/hooks';
import {
  AAG_SOD_ENTITLEMENT,
  AAG_SOD_VIOLATIONS,
  CF_NEU_STL,
  ENTITLEMENT_DETAILS_TABS,
  ENTITLEMENT_TAB_OPTIONS,
  RISK_DETAILS_TAB_OPTIONS,
  TABS_FILTERS,
  testIds,
  VIOLATION_TAB_OPTIONS,
  VIOLATIONS_DETAILS_TABS,
  violationsEntitlement,
  violationsHome,
} from '../../shared/constants';
import {
  useGetQueryParams,
  useGetRouteParams,
  useInvertedLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../shared/hooks';
import { detailsActions } from '../../shared/store/actions';
import { type RootState } from '../../shared/types';
import {
  createDevRejectionHandler,
  getDetailsSourceFromPathname,
} from '../../shared/utils';

import './Details.css';

/**
 * Details component that renders Link to SodMapped.
 *
 * @function
 * @returns {React.ReactElement} The rendered section containing the dummy components.
 *
 * TODO:
 * - Chip Status in header to be dynamic based on Violation status
 * - Mitigate Tab count needs to be dynamic
 * - Mitigating Control tab need to conditionally render based on the Violation status
 * - fix the SOD Header text truncation, it is broken on hover
 * - Sod Header for Ent need to have breadcrumbs above the title
 * - activeTab needs to be dynamic based on entry point, set on entrence, default by param source
 */
function Details(): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const { qId } = useGetRouteParams();
  const { activeTab: activeTabParam } = useGetQueryParams();
  const { updateActiveTab, navigateToUrl, navigateToBack } = useNavigateTo();

  const {
    user: { isMenuPinned },
    aag: {
      global: { dataTheme },
      violationsTab: {
        controls: { tabInAction },
      },
      details: {
        activeTab: detailsPageActiveTab,
        source,
        navigationContext,
        violationsTab: { actionManager },
      },
    },
  } = useTypedSelector((state) => state);

  const effectiveSource = useMemo(() => {
    if (source !== null) {
      return source;
    }

    if (!qId) {
      return null;
    }

    return getDetailsSourceFromPathname(window.location.pathname);
  }, [source, qId]);

  const title = navigationContext.title ?? actionManager?.title ?? '';
  const userName = navigationContext.userName ?? actionManager?.userName ?? '';

  // Tabs inside details page - moved before selector to avoid circular dependency
  const activeTab = useMemo(() => {
    if (activeTabParam) {
      return activeTabParam;
    }

    if (effectiveSource === violationsEntitlement) {
      return ENTITLEMENT_DETAILS_TABS.TAB_SUMMARY;
    }

    return VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS;
  }, [activeTabParam, effectiveSource]);

  const selector = useMemo(
    () => (state: RootState) => ({
      risksTotalRecords:
        state.aag.details.entitlementSummary.selectedEntitlement?.riskCount ??
        state.aag.details.entitlementRisks.list.data.length,
      usersTotalRecords:
        state.aag.details.entitlementSummary.selectedEntitlement?.userCount ??
        state.aag.details.entitlementUsers.list.data.length,
      violationsTotalRecords: (() => {
        const { entName, riskName } = state.aag.details.navigationContext;
        const hasEntitlementViolationsFilters =
          Boolean(entName) || Boolean(riskName);
        const isViolationsTab =
          activeTab === ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS;

        if (isViolationsTab && hasEntitlementViolationsFilters) {
          const entitlementViolationsTabFilters =
            state.aag.details.entitlementViolationsTab.tabFilter;

          if (entitlementViolationsTabFilters.length > 0) {
            return entitlementViolationsTabFilters.reduce(
              (total: number, filter: { count?: number }) =>
                total + (filter.count ?? 0),
              0
            );
          }
        }

        // Fallback to selectedEntitlement violationCount
        return state.aag.details.entitlementSummary.selectedEntitlement
          ?.violationCount;
      })(),
      entitlementName:
        state.aag.details.entitlementSummary.selectedEntitlement
          ?.entitlementDisplayName,
      selectedRiskName: (() => {
        const isRiskDetailsActive =
          activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS ||
          activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_SUMMARY;

        if (isRiskDetailsActive) {
          return state.aag.details.entitlementConflicts.selectedRisk?.riskName;
        }

        return (
          state.aag.details.entitlementRisks.selectedEntitlementRisk
            ?.riskName ??
          state.aag.details.entitlementRisks.list.data[0]?.riskName
        );
      })(),
    }),
    [activeTab]
  );

  const {
    risksTotalRecords,
    usersTotalRecords,
    violationsTotalRecords,
    entitlementName,
    selectedRiskName,
  } = useSelector(selector);

  const detailsPageClasses = cx(
    'Sod-Details',
    isMenuPinned && 'Sod-Details--isMenuPinned'
  );

  const commonBodyWrapperClasses = 'Sod-Details-commonMainWrapper';

  useEffect(() => {
    dispatch(detailsActions.setQueryParam(qId));
  }, [qId, dispatch]);

  // Restore source from URL on refresh so details content renders without redirecting.
  useEffect(() => {
    if (source !== null || !qId) {
      return;
    }

    const pathSource = getDetailsSourceFromPathname(window.location.pathname);

    if (pathSource) {
      dispatch(
        detailsActions.setDetailsNavigation({
          source: pathSource,
        })
      );
    }
  }, [source, qId, dispatch]);

  useHydrateViolationsDetailsSession(qId, effectiveSource);

  // For Badge count of mitigating control tab
  const { loadMitigateRow, mitigatingControlRows } =
    useHandleMitigatingControl();

  useEffect(() => {
    if (
      effectiveSource === violationsHome &&
      qId &&
      tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value
    ) {
      const id = parseInt(qId, 10);

      if (!Number.isInteger(id)) {
        return;
      }

      const requestData = {
        id,
        violationId: qId,
      };

      loadMitigateRow(requestData).catch((err: unknown) => {
        createDevRejectionHandler('Details: loadMitigateRow')(err);
      });
    }
  }, [effectiveSource, qId, loadMitigateRow, tabInAction]);

  useEffect(() => {
    if (
      !actionManager ||
      mitigatingControlRows.length === 0 ||
      actionManager.mitigatingControlCount === mitigatingControlRows.length
    ) {
      return;
    }

    dispatch(
      detailsActions.setViolationTabdetailsActionManager({
        ...actionManager,
        mitigatingControlCount: mitigatingControlRows.length,
      })
    );
  }, [actionManager, mitigatingControlRows.length, dispatch]);

  // Details tab
  const tabOptions = useMemo(() => {
    if (effectiveSource === violationsEntitlement) {
      // Check if in risk details view
      const isRiskDetailsActive =
        activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_SUMMARY ||
        activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS;

      if (isRiskDetailsActive) {
        // Show only conflicts and summary tabs for risk details
        return RISK_DETAILS_TAB_OPTIONS;
      }

      return ENTITLEMENT_TAB_OPTIONS.map((tab) => {
        if (
          tab.value === ENTITLEMENT_DETAILS_TABS.TAB_RISKS &&
          risksTotalRecords &&
          risksTotalRecords > 0
        ) {
          return {
            ...tab,
            badgeCount: risksTotalRecords,
          };
        }

        if (
          tab.value === ENTITLEMENT_DETAILS_TABS.TAB_USERS &&
          usersTotalRecords &&
          usersTotalRecords > 0
        ) {
          return {
            ...tab,
            badgeCount: usersTotalRecords,
          };
        }

        if (
          tab.value === ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS &&
          violationsTotalRecords &&
          violationsTotalRecords > 0
        ) {
          return {
            ...tab,
            badgeCount: violationsTotalRecords,
          };
        }

        return tab;
      });
    }

    if (tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value) {
      return VIOLATION_TAB_OPTIONS.map((tab) => {
        if (
          tab.value === VIOLATIONS_DETAILS_TABS.TAB_MITIGATING &&
          mitigatingControlRows.length > 0
        ) {
          return {
            ...tab,
            badgeCount: mitigatingControlRows.length,
          };
        }

        return tab;
      });
    }

    if (tabInAction === TABS_FILTERS.CLOSEDCOUNT.value) {
      return VIOLATION_TAB_OPTIONS.filter(
        (tab) =>
          tab.value !== VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS &&
          tab.value !== VIOLATIONS_DETAILS_TABS.TAB_MITIGATING
      );
    }

    return VIOLATION_TAB_OPTIONS.filter(
      (tab) => tab.value !== VIOLATIONS_DETAILS_TABS.TAB_MITIGATING
    );
  }, [
    effectiveSource,
    activeTab,
    tabInAction,
    mitigatingControlRows,
    risksTotalRecords,
    usersTotalRecords,
    violationsTotalRecords,
  ]);

  const handleChange = useCallback(
    (value: string) => {
      updateActiveTab(value);

      if (
        effectiveSource &&
        activeTab === ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS &&
        value !== ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS &&
        (navigationContext.entName ||
          navigationContext.riskName ||
          navigationContext.userName)
      ) {
        dispatch(
          detailsActions.setDetailsNavigation({
            source: effectiveSource,
            title: navigationContext.title,
            userName: navigationContext.userName,
          })
        );
      }
    },
    [updateActiveTab, activeTab, effectiveSource, navigationContext, dispatch]
  );

  // Header params
  const headerParams = useMemo(() => {
    switch (effectiveSource) {
      case violationsHome:
        return {
          title,
          description: (
            <Box
              tag='div'
              className='Sod-ViolationsTabDetails-headerParamDescriptionWrapper'>
              <Typography
                kind='body2'
                color={CF_NEU_STL}
                className='Sod-ViolationsTabDetails-headerParamViolationsBy'>
                {l('Violation by')}
              </Typography>
              <DSLink
                kind='button'
                text={`${userName}`}
                color='neutral'
                onClick={() => {
                  handleChange(VIOLATIONS_DETAILS_TABS.TAB_USER);
                }}
                className='Sod-ViolationsSidePanelBody-links'
              />
            </Box>
          ),
          tabInAction,
        };

      case violationsEntitlement: {
        const baseBreadcrumbs: {
          text: string;
          href?: string;
          onClick?: () => void;
        }[] = [
          { text: 'Violations', href: AAG_SOD_VIOLATIONS },
          {
            text: 'Entitlements with Inherent Risk',
            href: AAG_SOD_ENTITLEMENT,
          },
        ];

        // Additional breadcrumb if in risk details view
        const isRiskDetailsActive =
          activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_SUMMARY ||
          activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS;

        // Check if coming from risks/users tabs with filters to violations tab
        const isViolationsTabWithFilters =
          activeTab === ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS &&
          (Boolean(navigationContext.riskName) ||
            Boolean(navigationContext.userName));

        if (isRiskDetailsActive) {
          baseBreadcrumbs.push({
            text: entitlementName ?? title,
            onClick: () => updateActiveTab(ENTITLEMENT_DETAILS_TABS.TAB_RISKS),
          });
        } else if (isViolationsTabWithFilters) {
          const hasRiskName = Boolean(navigationContext.riskName);
          const hasUserName = Boolean(navigationContext.userName);

          baseBreadcrumbs.push({
            text: entitlementName ?? title,
            onClick: () => {
              let targetTab = ENTITLEMENT_DETAILS_TABS.TAB_SUMMARY;

              if (hasRiskName) {
                targetTab = ENTITLEMENT_DETAILS_TABS.TAB_RISKS;
              } else if (hasUserName) {
                targetTab = ENTITLEMENT_DETAILS_TABS.TAB_USERS;
              }

              dispatch(
                detailsActions.setDetailsNavigation({
                  source: effectiveSource,
                  title: navigationContext.title,
                  userName: navigationContext.userName,
                })
              );

              updateActiveTab(targetTab);
            },
          });
        }

        let pageTitle = entitlementName ?? title;

        if (isRiskDetailsActive) {
          pageTitle = selectedRiskName ?? title;
        } else if (isViolationsTabWithFilters) {
          if (navigationContext.riskName) {
            pageTitle = navigationContext.riskName;
          } else if (navigationContext.userName) {
            pageTitle = navigationContext.userName;
          }
        }

        return {
          title: pageTitle,
          description: null,
          breadcrumbItems: baseBreadcrumbs,
        };
      }

      default:
        return {
          title: l('Title'),
          description: l('Description'),
        };
    }
  }, [
    l,
    effectiveSource,
    title,
    userName,
    tabInAction,
    handleChange,
    entitlementName,
    activeTab,
    updateActiveTab,
    selectedRiskName,
    navigationContext.title,
    navigationContext.riskName,
    navigationContext.userName,
    dispatch,
  ]);

  const handleBackButton = useCallback(() => {
    // Check if in risk details view
    const isRiskDetailsActive =
      activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_SUMMARY ||
      activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS;

    if (effectiveSource === violationsEntitlement && isRiskDetailsActive) {
      // Go back to risks tab when in risk details view
      updateActiveTab(ENTITLEMENT_DETAILS_TABS.TAB_RISKS);

      return;
    }

    batch(() => {
      dispatch(detailsActions.setQueryParam(undefined));
      dispatch(detailsActions.setDetailsNavigation(null));
      dispatch(detailsActions.setSidePanelClose());
      dispatch(detailsActions.setViolationTabdetailsActionManager(null));

      // For conflict page controls
      dispatch(detailsActions.setViolationsTabActiveFilters([]));
      dispatch(detailsActions.setViolationsTabExpandKey(null));
    });

    if (detailsPageActiveTab === ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS) {
      navigateToUrl(AAG_SOD_ENTITLEMENT);

      return;
    }

    if (effectiveSource === violationsHome) {
      navigateToUrl(AAG_SOD_VIOLATIONS);

      return;
    }

    if (effectiveSource === violationsEntitlement) {
      navigateToUrl(AAG_SOD_ENTITLEMENT);

      return;
    }

    navigateToBack();
  }, [
    effectiveSource,
    dispatch,
    navigateToBack,
    navigateToUrl,
    activeTab,
    updateActiveTab,
    detailsPageActiveTab,
  ]);

  // Details Body
  const getContent = useCallback(() => {
    const violationsHistoryTabClasses = cx(
      commonBodyWrapperClasses,
      'Sod-ViolationsTabDetails-tabContent-historyTab'
    );

    switch (effectiveSource) {
      case violationsHome:
        return (
          <ViolationsTabDetails
            activeTab={activeTab}
            handleChange={handleChange}
            className={
              activeTab === VIOLATIONS_DETAILS_TABS.TAB_HISTORY
                ? violationsHistoryTabClasses
                : commonBodyWrapperClasses
            }
            handleBackButton={handleBackButton}
            username={userName}
          />
        );

      case violationsEntitlement:
        return (
          <EntitlementDetails
            activeTab={activeTab}
            className={commonBodyWrapperClasses}
          />
        );

      default:
        return <Loader kind='dots' format='inline' color='brand' />;
    }
  }, [activeTab, handleChange, effectiveSource, handleBackButton, userName]);

  return (
    <Box
      dataTheme={dataTheme}
      className={detailsPageClasses}
      dataTestId={testIds.SOD_DETAILS_PAGE}>
      {/* Header and Tabs */}
      <DetailsHeader
        params={headerParams}
        tabOptions={tabOptions}
        activeTab={activeTab}
        setActiveTab={handleChange}
        handleBackButton={handleBackButton}
      />
      {/* Tab Content / Scroll Area */}
      <article className='Sod-Details-innerMainWrapper'>{getContent()}</article>
    </Box>
  );
}

export default Details;
